import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useEntitlements } from "@/lib/useEntitlements";
import WelcomeCard from "@/components/WelcomeCard";
import {
  Loader2, Building2, Sparkles, Twitter, MapPin, Globe, Tag, IndianRupee,
  Plus, X, Check, Target, ExternalLink, RotateCcw, AlertTriangle, ArrowUpRight, Camera, Info,
  Pencil, Download, FileText, FileSpreadsheet, ChevronDown, Trash2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Brand Foundation — the confirmed brand's control room.
 * Keywords are the star: they decide what every scan searches, so they lead
 * the page with live tick/untick, stage grouping, add + delete.
 */
export default function BrandFoundation() {
  const navigate = useNavigate();
  // Phase 5.6 — no private copy of /workspaces/current, and the load is keyed
  // on the epoch. This page derived the brand independently (`find(confirmed)
  // || list[0]`) AND held its own workspace copy; both went stale on a switch
  // once the reload was gone.
  //
  // B3 — and it no longer derives the brand at all. `find(confirmed) || list[0]`
  // here was a FIFTH independent copy of "which brand is mine"; the moment a
  // workspace holds two brands, this page and the rest of the app can disagree
  // about which one is on screen, silently, with both looking authoritative.
  // The seated brand comes from context, which is the only place that rule
  // lives now.
  const {
    user,
    workspace: ctxWorkspace,
    tenantEpoch,
    brand: ctxBrand,
    brands: ctxBrands,
    brandLoading,
    loading: authLoading,
    refreshWorkspace,
    refreshBrand,
  } = useAuth();
  const epochRef = React.useRef(tenantEpoch);
  epochRef.current = tenantEpoch;
  const { loaded: entLoaded, canReset, brandAnalysisUnblocksAt } = useEntitlements();
  // Human "unblocks in ~Xh Ym" from the cooldown ISO timestamp.
  const resetUnblocksIn = useMemo(() => {
    if (!brandAnalysisUnblocksAt) return null;
    const ms = new Date(brandAnalysisUnblocksAt).getTime() - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h >= 1 ? `${h}h ${m}m` : `${m}m`;
  }, [brandAnalysisUnblocksAt]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [hasFirstScan, setHasFirstScan] = useState(false);
  const [error, setError] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [preparingReset, setPreparingReset] = useState(false);
  // Synced every render, and `preparingReset` is part of it deliberately:
  // `openResetDialog` awaits `refreshBrand()` BEFORE the dialog opens, so
  // without that term the effect would tear the page down during exactly the
  // window the "Checking…" spinner is meant to cover.
  const destructiveDialogOpenRef = useRef(false);
  destructiveDialogOpenRef.current = showReset || showDelete || preparingReset;
  // How many brands the workspace really holds. The wipe below destroys ALL of
  // them, so the count is not decoration — it is the fact that distinguishes
  // the two actions, and the one the old copy never showed.
  const brandCount = Array.isArray(ctxBrands) ? ctxBrands.length : 0;
  const otherCount = Math.max(0, brandCount - 1);

  const brandId = ctxBrand?.brand_id || null;

  // Every write below is guarded by the epoch captured before the first
  // request, re-checked after each await. This page has no cancel flag to lean
  // on for a brand change: it stays mounted, so only identity can tell a live
  // response from a stale one.
  const loadBrand = React.useCallback(async (id) => {
    const epoch = epochRef.current;
    const stale = () => epochRef.current !== epoch;
    try {
      if (!id) {
        // The workspace comes from context now — fetching a second copy here is
        // what let this page name one tenant while showing another's brand.
        const stats = await api
          .get("/dashboard/stats")
          .then((r) => r.data)
          .catch(() => null);
        if (stale()) return;
        setHasFirstScan((stats?.totals?.active_queries || 0) > 0);
        setData(null);
        return;
      }
      const full = await api.get(`/brands/${id}`).then((r) => r.data);
      if (stale()) return;
      setData(full);
    } catch (e) {
      if (stale()) return;
      setError(e?.response?.data?.detail || "Could not load your brand.");
    } finally {
      if (!stale()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Clear first: between two tenants this page shows a loader, never the
    // previous tenant's brand foundation.
    // A destructive dialog is open — do NOT tear this page down.
    //
    // `refreshBrand()` flips `brandLoading`, which re-runs this effect, which
    // nulls `data` and shows the full-page loader at the early return below.
    // Both destructive dialogs are rendered by THIS component, so that unmounts
    // the confirmation mid-flow: opening "Start over" blanked the screen, and
    // the 409 "nothing was deleted" refusal made the dialog vanish and the page
    // empty — a safety refusal that reads as a crash.
    //
    // Skipping is safe AND sufficient: the brand count the dialogs show comes
    // from `ctxBrands` (AuthContext), which `refreshBrand` updates independently
    // of anything this effect does. Nothing here is needed to refresh the count.
    // A real tenant/brand switch cannot be missed either — `clearBrandSeat`
    // nulls the seated brand, which drops this page to WelcomeCard above.
    if (destructiveDialogOpenRef.current) return;
    setData(null);
    setHasFirstScan(false);
    setError(null);
    setLoading(true);
    // While auth/brand are still settling we do NOT know whether this workspace
    // has a brand. Staying in the loader is the honest answer; running the
    // no-brand path here would flash "set up your brand" at someone who has one.
    if (authLoading || brandLoading) return;
    loadBrand(brandId);
  }, [loadBrand, brandId, tenantEpoch, authLoading, brandLoading]);

  if (loading) return <div className="flex items-center gap-2 text-lm-text2"><Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> Loading…</div>;
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50/60 p-4 text-sm text-red-700">{error}</div>;
  if (!data) {
    return <WelcomeCard workspace={ctxWorkspace} brand={null} hasFirstScan={hasFirstScan} userName={user?.name} />;
  }

  const { brand, profile, icps, keywords, competitors } = data;

  const setKeywords = (next) => setData((d) => ({ ...d, keywords: next }));

  // Delete exactly ONE brand — `DELETE /brands/{id}`.
  //
  // This endpoint existed and was correct from the day it was written, but
  // nothing in the app ever called it: the only removal control was the card
  // below, which posts to `/brands/reset` and wipes the whole workspace. Because
  // that card was worded "Delete this brand" and its modal named the brand you
  // were looking at, a customer deleting one brand lost all of them.
  const doDeleteBrand = async () => {
    setDeleting(true);
    try {
      await api.delete(`/brands/${brand.brand_id}`);
      await Promise.all([refreshWorkspace?.(), refreshBrand?.()]);
      toast.success(`${brand.name} deleted. Nothing else was touched.`);
      // Other brands remain → back to the app; last one gone → set up a new one.
      // When sending the user to onboarding, clear the skip flag for the same
      // reason `doReset` does: they are being asked to set a brand up, so a
      // stored "skip onboarding" would fight the destination. (Harmless today —
      // Onboarding's bounce-out only fires when a confirmed brand exists, and
      // there are now none — but the two handlers should not differ silently.)
      if (otherCount === 0) sessionStorage.removeItem("lm_skip_onboarding");
      // No setDeleting(false) on success: this component unmounts on navigate.
      // Resetting state on an unmounting component is a no-op at best.
      navigate(otherCount > 0 ? "/app/dashboard" : "/onboarding", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (typeof detail === "object" ? detail?.message : detail) ||
          "Couldn't delete that brand. Nothing was removed — try again."
      );
      setDeleting(false);
      setShowDelete(false);
    }
  };

  // Wipe the WHOLE workspace — `POST /brands/reset`. Every brand, not this one.
  // Open the wipe dialog only AFTER refreshing the brand list.
  //
  // `ctxBrands` is fetched once by `refreshBrand` and never revalidated — no
  // interval, no focus refetch. A tab left open while another tab adds brands
  // holds a stale-LOW count, which would both understate the blast radius and
  // silently remove the type-to-confirm gate (it only appears above one brand).
  // This closes the window; the server's count check is what actually enforces
  // it, since this refresh can still lose a race.
  const openResetDialog = async () => {
    setPreparingReset(true);
    try {
      await refreshBrand?.();
    } catch {
      /* stale-high is the safe direction — the server check backstops it */
    }
    setPreparingReset(false);
    setShowReset(true);
  };

  const doReset = async () => {
    setResetting(true);
    try {
      // Tell the server what the dialog claimed. It refuses on a mismatch
      // rather than deleting a number of brands the user never agreed to.
      await api.post("/brands/reset", { expected_brand_count: brandCount });
      await Promise.all([refreshWorkspace?.(), refreshBrand?.()]);
      sessionStorage.removeItem("lm_skip_onboarding");
      toast.success(
        `Workspace cleared — ${brandCount === 1 ? "1 brand" : `${brandCount} brands`} deleted. Let's set it up fresh.`
      );
      navigate("/onboarding", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setResetting(false);
      // The server refused because the workspace holds a different number of
      // brands than we showed. Nothing was deleted. Pull the truth and keep the
      // dialog OPEN so it re-renders with the real count — and, above one brand,
      // with the type gate that a stale-low count had hidden. Closing here would
      // make a near-miss look like a random failure.
      if (detail && typeof detail === "object" && detail.brand_count_mismatch) {
        await refreshBrand?.().catch(() => {});
        toast.error(detail.message || "Your brand list changed. Nothing was deleted.");
        return;
      }
      toast.error(
        (typeof detail === "object" ? detail?.message : detail) ||
          "Couldn't clear the workspace. Your brands are still here — try again."
      );
      setShowReset(false);
    }
  };

  return (
    <div className="pb-10">
      {/* Hero — social-style cover banner + circular avatar */}
      <BrandHero
        brandId={brand.brand_id}
        brand={brand}
        profile={profile}
        icps={icps || []}
        keywords={keywords || []}
        onBrandUpdate={(b) => setData((d) => ({ ...d, brand: b }))}
      />

      {/* KEYWORDS — the star of the page */}
      <KeywordsCard brandId={brand.brand_id} keywords={keywords || []} onChange={setKeywords} />

      {/* Brand details + ICPs — full-width, single column */}
      <div className="space-y-6 mt-6">
        <div className="rounded-xl border border-lm-border bg-lm-surface p-6">
          <SectionHead icon={Building2} title="Profile" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <KV label="Website" v={brand.website_url} mono />
            <KV label="Category" v={profile?.category} />
            <KV label="Geography" v={profile?.target_geography} />
            <KV label="Price point" v={profile?.price_point} />
          </div>
          {profile?.value_props?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase font-mono tracking-widest text-lm-text3 mb-2">Value props</div>
              <ul className="text-[15px] text-lm-text2 space-y-1.5">
                {profile.value_props.map((v, i) => (
                  <li key={i} className="flex gap-2">
                    <Check className="w-4 h-4 text-lm-primary mt-0.5 shrink-0" />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <IcpTabs icps={icps || []} />
      </div>

      {competitors?.length > 0 && (
        <div className="rounded-xl border border-lm-border bg-lm-surface p-6 mt-6">
          <h2 className="font-display font-semibold text-lg mb-4">Competitors</h2>
          <ul className="text-sm text-lm-text2 space-y-1">
            {competitors.map((c) => (
              <li key={c.competitor_id}>{c.name} {c.website_url && <span className="text-lm-text3 font-mono">- {c.website_url}</span>}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Market scope */}
      <div data-testid="brand-market-scope-card" className="rounded-xl border border-lm-border bg-lm-surface p-6 mt-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-lm-tint2 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-lm-primary" />
          </div>
          <div className="flex-1">
            <h2 className="font-display font-semibold text-lg mb-1">Market scope</h2>
            <p className="text-sm text-lm-text2 max-w-2xl">
              We plan every scan around who buys from you and where they live — the single biggest lever we tune for you.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <span data-testid="brand-market-scope-chip" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint2 text-lm-primary text-xs font-mono uppercase tracking-widest">
                {formatScope(brand.market_scope)}
              </span>
              {brand.market_city && (
                <span data-testid="brand-market-city-chip" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint text-lm-text2 text-xs font-mono">
                  <MapPin className="w-3 h-3" />
                  {brand.market_city}
                </span>
              )}
              {!brand.market_scope && <span className="text-xs text-lm-text3">Not set yet.</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Household-name brand — Coming soon (X/Twitter routing not wired yet) */}
      <div data-testid="brand-household-toggle-card" className="rounded-xl border border-lm-border bg-lm-surface p-6 mt-6 opacity-95">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-lm-tint2 flex items-center justify-center shrink-0">
              <Twitter className="w-4 h-4 text-lm-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-display font-semibold text-lg">Household-name brand</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-lm-tint2 text-lm-primary">
                  Coming soon
                </span>
              </div>
              <p className="text-sm text-lm-text2 max-w-2xl">
                For famous brands (Zomato, Zerodha, Boat), we'll re-enable X/Twitter in scans — where
                mentions of your name are almost never coincidental. For everyone else X stays off (91% noise).
              </p>
            </div>
          </div>
          <div
            aria-disabled="true"
            title="Coming soon"
            className="shrink-0 relative inline-flex items-center h-7 w-12 rounded-full bg-lm-border cursor-not-allowed opacity-60"
          >
            <span className="inline-block w-5 h-5 bg-white rounded-full shadow translate-x-1" />
          </div>
        </div>
      </div>

      {/* ── Danger zone A — delete THIS brand (scoped) ────────────────────── */}
      <div data-testid="brand-delete-card" className="rounded-xl border border-red-200 bg-red-50/40 p-6 mt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-lg mb-1">Delete this brand</h2>
              <p className="text-sm text-lm-text2 max-w-2xl">
                Permanently delete {brand.name} and everything under it — its profile, ideal
                customers, keywords, competitors, past scans and any leads you saved from it.
                This can't be undone.
                {otherCount > 0 && (
                  <>
                    {" "}Your other {otherCount === 1 ? "brand stays" : `${otherCount} brands stay`}{" "}
                    exactly as {otherCount === 1 ? "it is" : "they are"}.
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            data-testid="brand-delete-btn"
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-lg border border-red-300 bg-white text-red-600 px-4 py-2 text-sm font-semibold hover:bg-red-600 hover:text-white transition-colors shrink-0"
          >
            <Trash2 className="w-4 h-4" />
            Delete this brand
          </button>
        </div>
      </div>

      {/* ── Danger zone B — clear the WHOLE workspace ─────────────────────── */}
      <div data-testid="brand-reset-card" className="rounded-xl border border-red-300 bg-red-50/60 p-6 mt-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-lg mb-1">
                Start over — clear this whole workspace
              </h2>
              <p className="text-sm text-lm-text2 max-w-2xl">
                Delete{" "}
                <strong className="text-red-700">
                  {brandCount === 1 ? "your brand" : `all ${brandCount} brands in this workspace`}
                </strong>
                , {brandCount === 1 ? "its" : "their"} setup and scan results, and redo the 4-step
                setup from scratch. This can't be undone. It's free.
                {entLoaded && !canReset && resetUnblocksIn && (
                  <span className="block mt-1 text-amber-700 font-medium">
                    Just used — you can start over again in {resetUnblocksIn}.
                  </span>
                )}
              </p>
            </div>
          </div>
          {!entLoaded ? (
            <button
              disabled
              aria-busy="true"
              data-testid="brand-reset-pending"
              className="inline-flex items-center gap-2 rounded-lg border border-lm-border bg-lm-tint text-lm-text3 px-4 py-2 text-sm font-semibold cursor-default shrink-0"
            >
              <RotateCcw className="w-4 h-4" />
              Checking…
            </button>
          ) : canReset ? (
            <button
              data-testid="brand-reset-btn"
              onClick={openResetDialog}
              disabled={preparingReset}
              className="inline-flex items-center justify-center gap-2 min-h-[44px] rounded-lg border border-red-300 bg-white text-red-600 px-4 py-2 text-sm font-semibold hover:bg-red-600 hover:text-white transition-colors shrink-0 disabled:opacity-60"
            >
              {preparingReset ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> : <RotateCcw className="w-4 h-4" />}
              Start over…
            </button>
          ) : (
            <button
              disabled
              data-testid="brand-reset-cooldown"
              title="Available again once your last brand setup's 30-minute cooldown ends."
              className="inline-flex items-center gap-2 rounded-lg border border-lm-border bg-lm-tint text-lm-text3 px-4 py-2 text-sm font-semibold cursor-not-allowed shrink-0"
            >
              <RotateCcw className="w-4 h-4" />
              {resetUnblocksIn ? `Unblocks in ${resetUnblocksIn}` : "Start over…"}
            </button>
          )}
        </div>
      </div>

      {showDelete && (
        <DeleteBrandConfirm
          brandName={brand.name}
          otherCount={otherCount}
          deleting={deleting}
          onCancel={() => setShowDelete(false)}
          onConfirm={doDeleteBrand}
        />
      )}

      {showReset && (
        <ResetConfirm
          // Remount when the count changes, so `typed` is cleared. Without this,
          // a user who satisfied the gate for 1 brand and then learned (via the
          // 409) that there are really 5 would carry an already-passed gate into
          // a five-times-larger deletion. Previously masked by the page blanking
          // and destroying this component; that bug is fixed, so this is live.
          key={brandCount}
          brandCount={brandCount}
          brandName={brand.name}
          resetting={resetting}
          onCancel={() => setShowReset(false)}
          onConfirm={doReset}
        />
      )}
    </div>
  );
}

/**
 * Shared behaviour for the two destructive dialogs: Escape to cancel, a real
 * focus trap, and focus parked on the SAFE control on open.
 *
 * `aria-modal="true"` is an assertion, not an implementation — without this, Tab
 * walks straight out of the dialog into the page behind it, and Escape (which is
 * the reflex users actually have when a scary dialog appears) does nothing. On a
 * dialog that permanently deletes a workspace, "Escape means keep my brands" has
 * to be true.
 *
 * Returns refs to spread onto the dialog element and the safe button.
 */
function useDestructiveDialog({ onCancel, busy }) {
  const dialogRef = useRef(null);
  const safeRef = useRef(null);

  useEffect(() => {
    // Remember what had focus so it can be handed back on close — otherwise a
    // keyboard user who cancels is dumped on document.body and has to tab in
    // from the top of the page to get back to where they were.
    const returnTo = document.activeElement;
    safeRef.current?.focus();
    return () => {
      if (returnTo instanceof HTMLElement && document.contains(returnTo)) {
        returnTo.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // Never cancel mid-request: the delete may already be running server
        // side, and closing would tell the user nothing happened.
        if (!busy) onCancel?.();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Focus can sit OUTSIDE the dialog — a backdrop click while the request
      // is in flight (where onClick is undefined) moves it to body. Neither
      // edge test would then match and Tab would walk into the page behind the
      // modal. Pull it back in first.
      if (!dialogRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel, busy]);

  return { dialogRef, safeRef };
}

// Destructive dialogs animate in via `motion-safe:` so reduced-motion users get
// no transform — `fadeIn` includes a translateY and index.css has no global
// reduce rule, only class-scoped ones.
const DIALOG_ANIM = "motion-safe:animate-[fadeIn_0.2s_ease]";
// 44px minimum touch target on every control in these dialogs.
const BTN_BASE = "min-h-[44px] inline-flex items-center justify-center";

/** Confirm for deleting ONE brand. Scoped, and says so. */
function DeleteBrandConfirm({ brandName, otherCount, deleting, onCancel, onConfirm }) {
  const { dialogRef, safeRef } = useDestructiveDialog({ onCancel, busy: deleting });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={deleting ? undefined : onCancel}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-brand-title"
        className={`w-full max-w-md rounded-2xl bg-lm-surface border border-lm-border shadow-2xl p-6 ${DIALOG_ANIM}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <h3 id="delete-brand-title" className="text-xl font-display font-semibold mb-2">
          Delete {brandName || "this brand"}?
        </h3>
        <p className="text-sm text-lm-text2 leading-relaxed mb-1">
          This permanently deletes, for this brand only:
        </p>
        <ul className="text-sm text-lm-text2 mb-5 space-y-1">
          <li className="flex gap-2"><X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> its profile and summary</li>
          <li className="flex gap-2"><X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> its ideal customers and keywords</li>
          <li className="flex gap-2"><X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> its competitors</li>
          <li className="flex gap-2"><X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> every past scan and the leads those scans found</li>
          <li className="flex gap-2"><X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" /> any leads you saved from this brand</li>
        </ul>
        <p className="text-sm text-lm-text2 mb-6">
          {otherCount > 0
            ? `Your other ${otherCount === 1 ? "brand is" : `${otherCount} brands are`} not affected. This can't be undone.`
            : "This is your only brand. Your workspace and setup answers stay — you can add a new brand any time. This can't be undone."}
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            ref={safeRef}
            onClick={onCancel}
            disabled={deleting}
            className={`${BTN_BASE} px-4 py-2.5 rounded-lg text-sm font-medium text-lm-text2 hover:bg-lm-tint/60 transition-colors disabled:opacity-60`}
          >
            Keep it
          </button>
          <button
            data-testid="brand-delete-confirm"
            onClick={onConfirm}
            disabled={deleting}
            className={`${BTN_BASE} gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60`}
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> : <Trash2 className="w-4 h-4" />}
            {deleting ? "Deleting…" : "Delete this brand"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Confirm for wiping the WHOLE workspace.
 *
 * The previous version of this dialog was titled "Reset {brandName}?" and
 * listed only that brand's data, while the request behind it deleted every
 * brand in the workspace. A customer lost all of theirs to it. Two things are
 * therefore load-bearing here and must not be "simplified" away:
 *
 *   1. The real brand count appears in the title, the blast-radius line and the
 *      confirm button — never the name of the brand you happen to be viewing.
 *   2. With more than one brand at stake, a typed confirmation stands between
 *      the click and the deletion. Below that there is nothing extra to protect,
 *      so the gate would be friction for its own sake.
 */
function ResetConfirm({ brandCount, brandName, resetting, onCancel, onConfirm }) {
  const needsTypeGate = brandCount > 1;
  const [typed, setTyped] = useState("");
  const { dialogRef, safeRef } = useDestructiveDialog({ onCancel, busy: resetting });
  // Case-insensitive and trimmed: a capitalisation mismatch teaches nobody
  // anything and just punishes mobile keyboards.
  const confirmed = !needsTypeGate || typed.trim().toLowerCase() === "start over";
  const plural = brandCount === 1 ? "your brand" : `all ${brandCount} brands`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={resetting ? undefined : onCancel}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-ws-title"
        className={`w-full max-w-md rounded-2xl bg-lm-surface border border-lm-border shadow-2xl p-6 ${DIALOG_ANIM}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <h3 id="reset-ws-title" className="text-xl font-display font-semibold mb-2">
          Start over? This deletes {plural}.
        </h3>

        {/* The line that had to exist. */}
        <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
          {brandCount > 1
            ? `This is not just ${brandName}. Every brand in this workspace goes — all ${brandCount} of them.`
            : "This clears your whole workspace back to empty."}
        </p>

        <p className="text-sm text-lm-text2 leading-relaxed mb-1">This permanently deletes:</p>
        <ul className="text-sm text-lm-text2 mb-5 space-y-1">
          <li className="flex gap-2">
            <X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            {plural}, with their profiles, ideal customers, keywords and competitors
          </li>
          <li className="flex gap-2">
            <X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            every scan you've run, its results, and the leads you saved
          </li>
          <li className="flex gap-2">
            <X className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            your setup answers, so you'll pick your category and market again
          </li>
        </ul>
        <p className="text-sm text-lm-text2 mb-5">
          Then you start the 4-step setup fresh. This can't be undone.
        </p>

        {needsTypeGate && (
          <div className="mb-6">
            <label htmlFor="reset-type-gate" className="block text-sm font-medium text-lm-text mb-1.5">
              Type <span className="font-mono font-semibold">start over</span> to confirm
            </label>
            <input
              id="reset-type-gate"
              data-testid="brand-reset-typegate"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={resetting}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck="false"
              className="w-full rounded-lg border border-lm-border bg-white px-3.5 py-2 text-sm text-lm-text focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 disabled:opacity-60"
            />
            {typed.length > 0 && !confirmed && (
              <p className="mt-1.5 text-xs text-lm-text3">Type it exactly as shown to continue.</p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            ref={safeRef}
            onClick={onCancel}
            disabled={resetting}
            className={`${BTN_BASE} px-4 py-2.5 rounded-lg text-sm font-medium text-lm-text2 hover:bg-lm-tint/60 transition-colors disabled:opacity-60`}
          >
            Keep my brands
          </button>
          <button
            data-testid="brand-reset-confirm"
            onClick={onConfirm}
            disabled={resetting || !confirmed}
            className={`${BTN_BASE} gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" /> : <RotateCcw className="w-4 h-4" />}
            {resetting
              ? "Clearing your workspace…"
              : `Delete ${brandCount === 1 ? "my brand" : `all ${brandCount} brands`} and start over`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Keywords — prominent, tickable                                      */
/* ------------------------------------------------------------------ */

const STAGES = [
  { id: "ready", label: "Ready to buy", emoji: "🔥", dot: "bg-green-500", chip: "bg-green-100 text-green-700", hint: "hottest — reply with your offer" },
  { id: "researching", label: "Researching", emoji: "🌱", dot: "bg-amber-500", chip: "bg-amber-100 text-amber-700", hint: "comparing options — be helpful" },
  { id: "pain_aware", label: "Pain-aware", emoji: "🌊", dot: "bg-blue-500", chip: "bg-blue-100 text-blue-700", hint: "feels the problem — plant your flag" },
];

// Fair-use cap: at most 15 keywords active (ticked) at once — exactly what a
// scan searches. Tick a 16th → must untick one first. Keeps ticked == searched.
const MAX_ACTIVE = 15;

function KeywordsCard({ brandId, keywords, onChange }) {
  const [adding, setAdding] = useState("");
  const [addStage, setAddStage] = useState("ready");
  const [busy, setBusy] = useState(false);

  const isOn = (k) => k.default_selected !== false;
  const activeCount = useMemo(() => keywords.filter(isOn).length, [keywords]);
  const total = keywords.length;

  const grouped = useMemo(() => {
    const g = { ready: [], researching: [], pain_aware: [] };
    for (const k of keywords) (g[k.stage] || g.ready).push(k);
    return g;
  }, [keywords]);

  const toggle = async (kw) => {
    const turningOn = !isOn(kw);
    if (turningOn && activeCount >= MAX_ACTIVE) {
      toast.error(`${MAX_ACTIVE} keywords active is the max a scan searches. Untick one first.`);
      return;
    }
    const next = keywords.map((k) => (k.keyword_id === kw.keyword_id ? { ...k, default_selected: turningOn } : k));
    onChange(next); // optimistic
    try {
      await api.patch(`/brands/${brandId}/keywords/${kw.keyword_id}`, { default_selected: turningOn });
    } catch (e) {
      onChange(keywords); // revert
      toast.error(e?.response?.data?.detail || "Could not update keyword.");
    }
  };

  const remove = async (kw) => {
    const prev = keywords;
    onChange(keywords.filter((k) => k.keyword_id !== kw.keyword_id));
    try {
      await api.delete(`/brands/${brandId}/keywords/${kw.keyword_id}`);
    } catch (e) {
      onChange(prev);
      toast.error(e?.response?.data?.detail || "Could not remove keyword.");
    }
  };

  const add = async () => {
    const text = adding.trim();
    if (text.length < 2) return;
    const willTick = activeCount < MAX_ACTIVE; // over cap → store it inactive
    setBusy(true);
    try {
      const r = await api.post(`/brands/${brandId}/keywords`, { text, stage: addStage, default_selected: willTick });
      if (r.data?.created === false) {
        toast.info("That keyword is already there.");
      } else {
        onChange([...keywords, r.data.keyword]);
        setAdding("");
        if (!willTick) toast.info(`Added, but inactive — you already have ${MAX_ACTIVE} active. Untick one to use it.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add keyword.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-lm-primary/25 bg-gradient-to-br from-lm-tint/50 to-lm-surface p-5 md:p-6 shadow-[0_10px_30px_-16px_rgba(15,85,53,0.3)]">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-lm-primary text-white flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-xl leading-tight inline-flex items-center gap-1.5">
              Buyer keywords
              <InfoTip text="Ticked keywords are what every scan searches. Each one is searched separately, so more active keywords = a more thorough but slower scan. Untick some for a faster run." />
            </h2>
            <p className="text-xs text-lm-text2">Ticked keywords are exactly what every scan searches — up to {MAX_ACTIVE}.</p>
          </div>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lm-surface border border-lm-primary/30">
          <span className="text-lg font-display font-bold text-lm-primary leading-none">{activeCount}</span>
          <span className="text-xs text-lm-text2">of {MAX_ACTIVE} active{total > MAX_ACTIVE ? ` · ${total} saved` : ""}</span>
        </div>
      </div>

      <div className="mt-4 space-y-5">
        {STAGES.map((s) => {
          const items = grouped[s.id] || [];
          if (items.length === 0) return null;
          const on = items.filter(isOn).length;
          return (
            <div key={s.id}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                <span className="text-sm font-semibold text-lm-text">{s.emoji} {s.label}</span>
                <InfoTip text={s.hint} />
                <span className="ml-auto text-xs font-mono text-lm-text3">{on}/{items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((k) => {
                  const active = isOn(k);
                  return (
                    <div
                      key={k.keyword_id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={active}
                      onClick={() => toggle(k)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(k); } }}
                      className={`group flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer select-none transition-all ${
                        active
                          ? "border-lm-primary/40 bg-lm-surface ring-1 ring-lm-primary/10 hover:border-lm-primary/60"
                          : "border-lm-border bg-lm-bg/60 hover:border-lm-primary/40 hover:bg-lm-surface"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 border transition-colors ${
                          active ? "bg-lm-primary border-lm-primary text-white" : "bg-white border-lm-border text-transparent group-hover:border-lm-primary/50"
                        }`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span className={`flex-1 text-sm ${active ? "text-lm-text" : "text-lm-text3 line-through"}`}>{k.text}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); remove(k); }}
                        aria-label="Remove keyword"
                        className="opacity-0 group-hover:opacity-100 text-lm-text3 hover:text-red-600 transition-opacity shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add your own */}
      <div className="mt-5 pt-4 border-t border-lm-border/70">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <select
            value={addStage}
            onChange={(e) => setAddStage(e.target.value)}
            className="rounded-lg border border-lm-border bg-white px-3 py-2 text-sm text-lm-text focus:outline-none focus:ring-2 focus:ring-lm-primary/20"
          >
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.emoji} {s.label}</option>)}
          </select>
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add a phrase people actually type…"
            className="flex-1 rounded-lg border border-lm-border bg-white px-3.5 py-2 text-sm text-lm-text placeholder:text-lm-text3 focus:outline-none focus:ring-2 focus:ring-lm-primary/20 focus:border-lm-primary"
          />
          <button
            type="button"
            onClick={add}
            disabled={busy || adding.trim().length < 2}
            className="inline-flex items-center justify-center gap-1.5 bg-lm-primary text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-lm-primaryHover transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ideal customers — creative tabs                                     */
/* ------------------------------------------------------------------ */

function IcpTabs({ icps, onEdit }) {
  const [active, setActive] = useState(0);

  if (!icps.length) {
    return (
      <div className="rounded-xl border border-lm-border bg-lm-surface p-6">
        <SectionHead icon={Target} title="Ideal customers" onEdit={onEdit} />
        <p className="text-base text-lm-text2">No ideal customers configured.</p>
      </div>
    );
  }

  const icp = icps[Math.min(active, icps.length - 1)];

  return (
    <div className="rounded-xl border border-lm-border bg-lm-surface overflow-hidden flex flex-col">
      <div className="p-6 pb-0">
        <SectionHead icon={Target} title="Ideal customers" note={`${icps.length} profile${icps.length > 1 ? "s" : ""}`} onEdit={onEdit} />
      </div>

      {/* Tab strip — small button-style pills, in a row */}
      <div className="px-6 pb-4 flex flex-wrap gap-2">
        {icps.map((it, i) => {
          const on = i === active;
          return (
            <button
              key={it.icp_id || i}
              type="button"
              onClick={() => setActive(i)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                on
                  ? "bg-lm-primary text-white border-lm-primary"
                  : "bg-white text-lm-text2 border-lm-border hover:border-lm-primary/50 hover:text-lm-text"
              }`}
            >
              {it.name}
              <ArrowUpRight className={`w-3.5 h-3.5 ${on ? "text-white" : "text-lm-text3"}`} />
            </button>
          );
        })}
      </div>

      {/* Active panel — normal body font */}
      <div key={icp.icp_id || active} className="px-6 pb-6 pt-5 border-t border-lm-border animate-[fadeIn_0.25s_ease] flex-1">
        {icp.description && <p className="text-base text-lm-text2 leading-relaxed mb-5">{icp.description}</p>}

        {icp.role_titles?.length > 0 && (
          <div className="mb-5">
            <ChipLabel>Who they are</ChipLabel>
            <div className="flex flex-wrap gap-2">
              {icp.role_titles.map((t, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-lm-tint2 text-lm-primary text-sm font-medium">{t}</span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {icp.pain_points?.length > 0 && (
            <div>
              <ChipLabel>🩹 Pain points</ChipLabel>
              <ul className="space-y-1.5">
                {icp.pain_points.map((p, i) => (
                  <li key={i} className="text-[15px] text-lm-text2 flex gap-2">
                    <span className="text-red-400 mt-0.5">•</span><span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {icp.buying_triggers?.length > 0 && (
            <div>
              <ChipLabel>⚡ Buying triggers</ChipLabel>
              <ul className="space-y-1.5">
                {icp.buying_triggers.map((t, i) => (
                  <li key={i} className="text-[15px] text-lm-text2 flex gap-2">
                    <span className="text-lm-primary mt-0.5">•</span><span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {(icp.industries?.length > 0 || icp.company_sizes?.length > 0) && (
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-5 pt-5 border-t border-lm-border">
            {icp.industries?.length > 0 && (
              <div>
                <ChipLabel>Industries</ChipLabel>
                <div className="text-[15px] text-lm-text2">{icp.industries.join(" · ")}</div>
              </div>
            )}
            {icp.company_sizes?.length > 0 && (
              <div>
                <ChipLabel>Company size</ChipLabel>
                <div className="text-[15px] text-lm-text2">{icp.company_sizes.join(" · ")}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHead({ icon: Icon, title, note, onEdit }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-lg bg-lm-tint2 flex items-center justify-center">
        <Icon className="w-4 h-4 text-lm-primary" />
      </div>
      <h2 className="font-display font-semibold text-lg">{title}</h2>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-lm-text3 hover:text-lm-primary hover:bg-lm-tint2/60 transition-colors"
          aria-label={`Edit ${title}`}
          title={`Edit ${title}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      {note && <span className="ml-auto text-xs font-mono text-lm-text3 uppercase tracking-widest">{note}</span>}
    </div>
  );
}

function ChipLabel({ children }) {
  return <div className="text-[11px] uppercase font-mono tracking-widest text-lm-text3 mb-2">{children}</div>;
}

function InfoTip({ text }) {
  return (
    <span className="relative inline-flex items-center group/tip" tabIndex={0}>
      <Info className="w-3.5 h-3.5 text-lm-text3 cursor-help hover:text-lm-primary transition-colors" />
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-30 w-max max-w-[220px] rounded-md bg-[color:var(--lm-dark)] text-white text-xs leading-snug px-2.5 py-1.5 text-center shadow-lg opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible group-focus/tip:opacity-100 group-focus/tip:visible transition-opacity"
      >
        {text}
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Hero — cover banner + circular avatar (social style, uploadable)    */
/* ------------------------------------------------------------------ */

function BrandHero({ brandId, brand, profile, icps, keywords, onBrandUpdate }) {
  const [uploading, setUploading] = useState(null); // 'cover' | 'logo' | null
  const coverRef = useRef(null);
  const logoRef = useRef(null);
  const score = fitScore(profile);

  const onFile = async (e, field, maxW, maxH) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setUploading(field);
    try {
      const dataUrl = await resizeImage(file, maxW, maxH);
      const key = field === "cover" ? "cover_image" : "logo_image";
      const r = await api.patch(`/brands/${brandId}`, { [key]: dataUrl });
      onBrandUpdate(r.data.brand);
      toast.success(field === "cover" ? "Cover updated." : "Logo updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload image.");
    } finally {
      setUploading(null);
    }
  };

  const cover = brand.cover_image;
  const logo = brand.logo_image;
  const initial = (brand.name || "?").trim().charAt(0).toUpperCase();
  const seed = encodeURIComponent(brandId || brand.name || "leadmato");
  // No custom cover yet → the fixed Leadmato hero image (same as the homepage hero).
  // User can still replace it via "Add cover".
  const coverSrc = cover || "/hero-bg.jpg";
  // No custom logo yet → a modern generative avatar (DiceBear), seeded per brand.
  const logoSrc = logo || `https://api.dicebear.com/9.x/shapes/svg?seed=${seed}&backgroundType=gradientLinear`;

  return (
    <div className="mb-8">
      {/* Cover banner — full-bleed: cancels the main container's p-6/8/10 padding */}
      <div className="relative h-44 md:h-60 -mx-6 md:-mx-8 lg:-mx-10 -mt-6 md:-mt-8 lg:-mt-10 overflow-hidden bg-gradient-to-r from-lm-primary to-[color:var(--lm-dark)]">
        <img
          src={coverSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
        <button
          type="button"
          onClick={() => coverRef.current?.click()}
          disabled={uploading === "cover"}
          className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-lg bg-black/45 hover:bg-black/60 text-white text-xs font-medium px-3 py-1.5 backdrop-blur transition-colors disabled:opacity-70"
        >
          {uploading === "cover" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {cover ? "Change cover" : "Add cover"}
        </button>
        <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "cover", 1600, 520)} />
      </div>

      {/* Circular avatar — half on the cover, half below */}
      <div className="-mt-12 relative">
        <div className="relative inline-block">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-lm-bg bg-lm-surface overflow-hidden shadow-lg flex items-center justify-center">
            <span className="absolute text-4xl font-display font-bold text-lm-primary">{initial}</span>
            <img
              src={logoSrc}
              alt={brand.name}
              className="relative w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <button
            type="button"
            onClick={() => logoRef.current?.click()}
            disabled={uploading === "logo"}
            aria-label="Change logo"
            className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-lm-primary text-white flex items-center justify-center shadow hover:bg-lm-primaryHover transition-colors disabled:opacity-70"
          >
            {uploading === "logo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "logo", 400, 400)} />
        </div>
      </div>

      {/* Heading block (name + summary + badges) with the score meter on its right */}
      <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{brand.name}</h1>
          {profile?.brand_summary && <p className="text-lm-text2 max-w-2xl mt-1">{profile.brand_summary}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {profile?.category && <Pill icon={Tag}>{profile.category}</Pill>}
            {profile?.target_geography && <Pill icon={Globe}>{profile.target_geography}</Pill>}
            {profile?.price_point && <Pill icon={IndianRupee}>{profile.price_point}</Pill>}
            {brand.website_url && (
              <a
                href={brand.website_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint text-lm-text2 text-xs font-mono hover:text-lm-primary transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                {prettyUrl(brand.website_url)}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-3 shrink-0 sm:w-56">
          <ScoreMeter score={score} />
          <ExportMenu brand={brand} profile={profile} icps={icps} keywords={keywords} score={score} />
        </div>
      </div>
    </div>
  );
}

/* ---- Brand-fit score + export ---- */

function fitScore(profile) {
  const raw = Number(profile?.fit_score) || 0;
  if (raw > 0 && raw <= 100) return raw;
  return 70;
}

function scoreBand(score) {
  if (score >= 80) return { label: "Awesome", text: "text-green-700", fill: "bg-green-500" };
  if (score >= 55) return { label: "Good", text: "text-blue-700", fill: "bg-blue-500" };
  return { label: "Average", text: "text-amber-700", fill: "bg-amber-600" };
}

function ScoreMeter({ score }) {
  const band = scoreBand(score);
  const pos = Math.min(100, Math.max(0, score));
  return (
    <div className="w-full sm:w-56">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Brand fit</span>
        <span className={`text-xs font-bold ${band.text}`}>{band.label}</span>
      </div>
      <div className="flex items-center gap-2.5">
        <span className="text-2xl font-display font-bold leading-none tabular-nums text-lm-text">{score}</span>
        <div className="flex-1 h-2 rounded-full bg-lm-border/70 overflow-hidden">
          <div className={`h-full rounded-full ${band.fill}`} style={{ width: `${pos}%` }} />
        </div>
      </div>
    </div>
  );
}

function ExportMenu({ brand, profile, icps, keywords, score }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const doCSV = () => { exportBrandCSV(brand, profile, icps, keywords, score); setOpen(false); };
  const doPDF = () => { exportBrandPDF(brand, profile, icps, keywords, score); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-lm-border bg-lm-surface text-lm-text2 hover:text-lm-text hover:border-lm-primary/40 px-3 py-1.5 text-xs font-medium transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-lm-surface border border-lm-border rounded-lg shadow-lg py-1 z-30">
          <button onClick={doCSV} className="w-full text-left px-3 py-2 text-sm text-lm-text hover:bg-lm-tint/60 inline-flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-lm-primary" /> Export CSV
          </button>
          <button onClick={doPDF} className="w-full text-left px-3 py-2 text-sm text-lm-text hover:bg-lm-tint/60 inline-flex items-center gap-2">
            <FileText className="w-4 h-4 text-lm-primary" /> Export PDF
          </button>
        </div>
      )}
    </div>
  );
}

function exportBrandCSV(brand, profile, icps, keywords, score) {
  const dot = (arr) => (arr || []).filter(Boolean).join(" • ");
  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const rows = [
    ["Leadmato — Brand Sheet", ""],
    ["Generated", dateStr],
    ["", ""],
    ["BRAND", ""],
    ["Name", brand.name || ""],
    ["Website", brand.website_url || ""],
    ["Category", profile?.category || ""],
    ["Summary", profile?.brand_summary || ""],
    ["Geography", profile?.target_geography || ""],
    ["Price point", profile?.price_point || ""],
    ["Brand-fit score", `${score}/100 (${scoreBand(score).label})`],
    ["", ""],
    ["VALUE PROPS", ""],
    ["Value props", dot(profile?.value_props)],
    ["", ""],
    ["IDEAL CUSTOMERS", ""],
  ];
  (icps || []).forEach((icp, i) => {
    rows.push([`${i + 1}. Name`, icp.name || ""]);
    rows.push(["   Description", icp.description || ""]);
    rows.push(["   Pains", dot(icp.pain_points)]);
    rows.push(["   Triggers", dot(icp.buying_triggers)]);
    rows.push(["", ""]);
  });
  rows.push(["KEYWORDS", ""]);
  STAGES.forEach((s) => {
    const items = (keywords || []).filter((k) => (k.stage || "ready") === s.id);
    if (items.length) rows.push([s.label, dot(items.map((k) => k.text))]);
  });
  rows.push(["Active (searched)", dot((keywords || []).filter((k) => k.default_selected !== false).map((k) => k.text))]);
  rows.push(["", ""]);
  rows.push(["MARKET SCOPE", ""]);
  rows.push(["Scope", formatScope(brand.market_scope)]);
  if (brand.market_city) rows.push(["City", brand.market_city]);
  rows.push(["", ""]);
  rows.push(["Generated by Leadmato · leadmato.com", "one scan, five lenses"]);

  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(brand.name || "brand").replace(/\s+/g, "-").toLowerCase()}-leadmato.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportBrandPDF(brand, profile, icps, keywords, score) {
  const w = window.open("", "_blank", "width=840,height=1060");
  if (!w) { toast.error("Allow pop-ups to export the PDF."); return; }
  const esc = (s) => String(s ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const list = (arr) => (arr && arr.length ? "<ul>" + arr.map((x) => `<li>${esc(x)}</li>`).join("") + "</ul>" : "<p class='muted'>—</p>");
  const icpHtml = (icps || []).map((i, idx) =>
    `<div class="icp"><h3>${esc(i.name || "ICP " + (idx + 1))}</h3>` +
    (i.description ? `<p class="d">${esc(i.description)}</p>` : "") +
    (i.pain_points?.length ? `<p class="mini">Pain points</p>${list(i.pain_points)}` : "") +
    (i.buying_triggers?.length ? `<p class="mini">Buying triggers</p>${list(i.buying_triggers)}` : "") +
    `</div>`).join("");
  const kwHtml = STAGES.map((s) => {
    const items = (keywords || []).filter((k) => (k.stage || "ready") === s.id);
    if (!items.length) return "";
    const tgs = items.map((k) => `<span class="tag${k.default_selected === false ? " off" : ""}">${esc(k.text)}</span>`).join("");
    return `<div class="kwgrp"><span class="kwlabel">${s.emoji} ${esc(s.label)}</span><div class="tags">${tgs}</div></div>`;
  }).join("") || "<p class='muted'>—</p>";
  const band = scoreBand(score).label;
  const bandColor = score >= 80 ? "#0f5535" : score >= 55 ? "#2563EB" : "#D97706";
  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(brand.name)} — Leadmato Brand Sheet</title>
  <style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin:0; padding:0; }
    body { font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
           color:#2A2A28; font-size:12px; line-height:1.6; background:#fff; }
    @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
                   .icp, .kwgrp, h2 { page-break-inside:avoid; } }

    .band { background:#123D2A; color:#fff; padding:32px 46px; display:flex; justify-content:space-between; align-items:flex-end; gap:24px; }
    .brandmark { font-size:11px; letter-spacing:.3em; text-transform:uppercase; color:#8FD3B4; font-weight:700; }
    .band h1 { font-size:32px; margin:10px 0 6px; letter-spacing:-.02em; font-weight:700; line-height:1.1; }
    .band .sub { color:rgba(255,255,255,.72); font-size:13px; }
    .fitcard { background:#fff; border-radius:14px; padding:14px 20px; text-align:center; min-width:112px; }
    .fitcard .num { font-size:32px; font-weight:800; line-height:1; }
    .fitcard .lab { font-size:10px; letter-spacing:.14em; text-transform:uppercase; color:#8A8778; margin-top:6px; font-weight:700; }

    .body { padding:32px 46px 44px; }
    .lead { font-size:15px; color:#3a3a37; margin:0 0 26px; max-width:660px; }
    .stats { display:flex; gap:14px; }
    .stat { flex:1; border:1px solid #E7E4D8; border-radius:12px; padding:15px 17px; background:#FBFAF6; }
    .stat .k { font-size:10px; letter-spacing:.12em; text-transform:uppercase; color:#8A8778; font-weight:700; }
    .stat .v { font-size:16px; font-weight:700; color:#123D2A; margin-top:6px; text-transform:capitalize; }

    h2 { font-size:15px; color:#123D2A; margin:32px 0 15px; padding-bottom:10px; border-bottom:2px solid #EAE7DC;
         display:flex; align-items:center; gap:9px; font-weight:700; }
    h2:before { content:''; width:11px; height:11px; border-radius:3px; background:#D95A40; }
    ul { margin:0; padding-left:20px; } li { margin:6px 0; }
    .muted { color:#b0aea4; }

    .icps { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
    .icp { border:1px solid #E7E4D8; border-radius:12px; padding:16px 18px; background:#FBFAF6; }
    .icp h3 { font-size:15px; margin:0 0 5px; color:#123D2A; font-weight:700; }
    .icp .d { color:#6a6a64; font-size:12.5px; margin:0 0 10px; }
    .mini { font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:#8A8778; margin:10px 0 4px; font-weight:700; }
    .icp ul { padding-left:18px; } .icp li { margin:3px 0; font-size:12.5px; }

    .kwgrp { margin-bottom:16px; }
    .kwlabel { font-size:13px; font-weight:700; color:#3a3a37; margin-bottom:8px; display:block; }
    .tags { display:flex; flex-wrap:wrap; gap:7px; }
    .tag { border:1px solid #CBE2D6; background:#EAF4EE; color:#0f5535; border-radius:999px; padding:5px 13px; font-size:12px; font-weight:500; }
    .tag.off { color:#9a988e; background:#F2F1EC; border-color:#E2DFD5; }

    .foot { background:#123D2A; color:rgba(255,255,255,.8); padding:16px 46px; font-size:11px;
            display:flex; justify-content:space-between; align-items:center; }
    .foot b { color:#fff; }
  </style></head><body>
    <div class="band">
      <div>
        <div class="brandmark">Leadmato · Brand Sheet</div>
        <h1>${esc(brand.name)}</h1>
        <div class="sub">${esc(profile?.category || "")}${brand.website_url ? " · " + esc(brand.website_url) : ""}</div>
      </div>
      <div class="fitcard"><div class="num" style="color:${bandColor}">${score}</div><div class="lab">${esc(band)} fit</div></div>
    </div>
    <div class="body">
      ${profile?.brand_summary ? `<p class="lead">${esc(profile.brand_summary)}</p>` : ""}
      <div class="stats">
        <div class="stat"><div class="k">Fit score</div><div class="v">${score}/100</div></div>
        <div class="stat"><div class="k">Geography</div><div class="v">${esc(profile?.target_geography || "—")}</div></div>
        <div class="stat"><div class="k">Price point</div><div class="v">${esc(profile?.price_point || "—")}</div></div>
        <div class="stat"><div class="k">Market scope</div><div class="v">${esc(formatScope(brand.market_scope))}${brand.market_city ? " · " + esc(brand.market_city) : ""}</div></div>
      </div>
      <h2>Value props</h2>${list(profile?.value_props)}
      <h2>Ideal customers</h2>${icpHtml ? `<div class="icps">${icpHtml}</div>` : "<p class='muted'>—</p>"}
      <h2>Buyer keywords</h2>${kwHtml}
    </div>
    <div class="foot"><span>Generated by <b>Leadmato</b> · leadmato.com — one scan, five lenses</span><span>${dateStr}</span></div>
  </body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (_) {} }, 350);
}

// Downscale a picked image client-side → small JPEG data URL (keeps docs light).
function resizeImage(file, maxW, maxH, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Pill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lm-tint2 text-lm-primary text-xs font-medium capitalize">
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
}

function KV({ label, v, mono }) {
  if (!v) return null;
  return (
    <div className="mb-2.5">
      <div className="text-[10px] uppercase font-mono tracking-widest text-lm-text3">{label}</div>
      <div className={mono ? "font-mono text-sm break-all" : "text-sm capitalize"}>{v}</div>
    </div>
  );
}

function prettyUrl(u) {
  try { return new URL(u).hostname.replace("www.", ""); } catch { return u; }
}

function formatScope(scope) {
  switch (scope) {
    case "local_city": return "My city";
    case "india": return "All India";
    case "us": return "United States";
    case "global": return "Global";
    default: return "Not set";
  }
}
